import { create } from 'zustand'
import { 
  collection, 
  doc, 
  addDoc, 
  getDoc, 
  getDocs, 
  updateDoc,
  setDoc,
  query, 
  where, 
  orderBy,
  limit,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore'
import { db } from '../services/firebase'
import toast from 'react-hot-toast'
import { useWalletStore } from './walletStore'

export const useQuinielaStore = create((set, get) => ({
  quinielas: [],
  currentQuiniela: null,
  loading: false,
  error: null,

  // Crear una nueva quiniela (solo operadores/admins)
  createQuiniela: async (quinielaData) => {
    set({ loading: true, error: null })
    try {
      const { 
        name, 
        description, 
        league, 
        season, 
        jornada,
        roundName, // Nombre completo de la jornada
        entryFee, 
        startDate, 
        endDate, 
        matchIds,
        matches, // Array de objetos de partidos completos
        maxLimit 
      } = quinielaData

      // Calcular fecha de cierre (1 hora antes del primer partido)
      const registrationCloseDate = new Date(startDate)
      registrationCloseDate.setHours(registrationCloseDate.getHours() - 1)

      // Calcular pool y comisión
      const commission = 0.10 // 10%

      const newQuiniela = {
        name,
        description,
        league,
        season,
        jornada: parseInt(jornada),
        roundName: roundName || `Regular Season - ${jornada}`, // Guardar nombre completo, con fallback
        entryFee: parseFloat(entryFee),
        prizePool: 0, // Se actualiza conforme se inscriben
        commission,
        status: 'upcoming',
        startDate: Timestamp.fromDate(new Date(startDate)),
        endDate: Timestamp.fromDate(new Date(endDate)),
        registrationCloseDate: Timestamp.fromDate(registrationCloseDate),
        matchIds,
        totalMatches: matchIds.length,
        participants: {
          count: 0,
          maxLimit: maxLimit ? parseInt(maxLimit) : null
        },
        prizes: {
          first: 0,
          second: 0,
          third: 0
        },
        createdBy: quinielaData.createdBy,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }

      const docRef = await addDoc(collection(db, 'quinielas'), newQuiniela)
      
      // Guardar los partidos completos en una subcolección
      if (matches && matches.length > 0) {
        const matchesPromises = matches.map(match => 
          setDoc(doc(db, 'quinielas', docRef.id, 'matches', match.id.toString()), {
            ...match,
            // Convertir fecha a Timestamp si es Date
            date: match.date instanceof Date ? Timestamp.fromDate(match.date) : match.date,
            result: null,
            createdAt: serverTimestamp()
          })
        )
        await Promise.all(matchesPromises)
      }
      
      toast.success('Quiniela creada exitosamente')
      
      set({ loading: false })
      return { success: true, id: docRef.id }
    } catch (error) {
      console.error('Error creating quiniela:', error)
      set({ error: error.message, loading: false })
      toast.error('Error al crear la quiniela')
      return { success: false, error: error.message }
    }
  },

  // Actualizar una quiniela existente (solo operadores/admins)
  updateQuiniela: async (quinielaId, quinielaData) => {
    set({ loading: true, error: null })
    try {
      const { 
        name, 
        description, 
        league, 
        season, 
        jornada, 
        entryFee, 
        startDate, 
        endDate, 
        matchIds,
        maxLimit 
      } = quinielaData

      // Calcular fecha de cierre (1 hora antes del primer partido)
      const registrationCloseDate = new Date(startDate)
      registrationCloseDate.setHours(registrationCloseDate.getHours() - 1)

      const updateData = {
        name,
        description,
        league,
        season,
        jornada: parseInt(jornada),
        entryFee: parseFloat(entryFee),
        startDate: Timestamp.fromDate(new Date(startDate)),
        endDate: Timestamp.fromDate(new Date(endDate)),
        registrationCloseDate: Timestamp.fromDate(registrationCloseDate),
        matchIds,
        totalMatches: matchIds.length,
        'participants.maxLimit': maxLimit ? parseInt(maxLimit) : null,
        updatedAt: serverTimestamp()
      }

      const docRef = doc(db, 'quinielas', quinielaId)
      await updateDoc(docRef, updateData)
      
      // Actualizar en el estado local
      set(state => ({
        quinielas: state.quinielas.map(q => 
          q.id === quinielaId ? { ...q, ...updateData } : q
        ),
        currentQuiniela: state.currentQuiniela?.id === quinielaId 
          ? { ...state.currentQuiniela, ...updateData }
          : state.currentQuiniela,
        loading: false
      }))

      toast.success('Quiniela actualizada exitosamente')
      return { success: true }
    } catch (error) {
      console.error('Error updating quiniela:', error)
      set({ error: error.message, loading: false })
      toast.error('Error al actualizar la quiniela')
      return { success: false, error: error.message }
    }
  },

  // Obtener todas las quinielas (con filtros opcionales)
  fetchQuinielas: async (filters = {}) => {
    set({ loading: true, error: null })
    try {
      let q = collection(db, 'quinielas')
      const constraints = []

      // Aplicar filtros
      if (filters.status) {
        constraints.push(where('status', '==', filters.status))
      }

      if (filters.league) {
        constraints.push(where('league', '==', filters.league))
      }

      // Ordenar por fecha de inicio
      constraints.push(orderBy('startDate', 'desc'))

      // Limitar resultados si se especifica
      if (filters.limit) {
        constraints.push(limit(filters.limit))
      }

      q = query(q, ...constraints)
      const querySnapshot = await getDocs(q)
      
      const quinielas = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))

      set({ quinielas, loading: false })
      return quinielas
    } catch (error) {
      console.error('Error fetching quinielas:', error)
      set({ error: error.message, loading: false })
      toast.error('Error al cargar las quinielas')
      return []
    }
  },

  // Obtener quiniela por ID
  fetchQuinielaById: async (quinielaId) => {
    set({ loading: true, error: null })
    try {
      const docRef = doc(db, 'quinielas', quinielaId)
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        const quiniela = {
          id: docSnap.id,
          ...docSnap.data()
        }
        set({ currentQuiniela: quiniela, loading: false })
        return { success: true, data: quiniela }
      } else {
        set({ error: 'Quiniela no encontrada', loading: false })
        toast.error('Quiniela no encontrada')
        return { success: false, error: 'Quiniela no encontrada' }
      }
    } catch (error) {
      console.error('Error fetching quiniela:', error)
      set({ error: error.message, loading: false })
      toast.error('Error al cargar la quiniela')
      return { success: false, error: error.message }
    }
  },

  // Actualizar estado de quiniela
  updateQuinielaStatus: async (quinielaId, newStatus) => {
    set({ loading: true, error: null })
    try {
      const docRef = doc(db, 'quinielas', quinielaId)
      await updateDoc(docRef, {
        status: newStatus,
        updatedAt: serverTimestamp()
      })

      // Actualizar en el estado local
      set(state => ({
        quinielas: state.quinielas.map(q => 
          q.id === quinielaId ? { ...q, status: newStatus } : q
        ),
        currentQuiniela: state.currentQuiniela?.id === quinielaId 
          ? { ...state.currentQuiniela, status: newStatus }
          : state.currentQuiniela,
        loading: false
      }))

      toast.success('Estado actualizado')
      return { success: true }
    } catch (error) {
      console.error('Error updating quiniela status:', error)
      set({ error: error.message, loading: false })
      toast.error('Error al actualizar el estado')
      return { success: false, error: error.message }
    }
  },

  // Calcular y actualizar premios
  calculatePrizes: (totalPool, commission = 0.10) => {
    const netPool = totalPool * (1 - commission)
    return {
      first: netPool * 0.8,   // 80%
      second: netPool * 0.15,  // 15%
      third: netPool * 0.05    // 5%
    }
  },

  // Actualizar pool y premios cuando se inscribe un usuario
  updatePoolAndPrizes: async (quinielaId, entryFee) => {
    try {
      const docRef = doc(db, 'quinielas', quinielaId)
      const docSnap = await getDoc(docRef)
      
      if (!docSnap.exists()) {
        throw new Error('Quiniela no encontrada')
      }

      const currentData = docSnap.data()
      const newPrizePool = currentData.prizePool + entryFee
      const newParticipantCount = currentData.participants.count + 1
      const prizes = get().calculatePrizes(newPrizePool, currentData.commission)

      await updateDoc(docRef, {
        prizePool: newPrizePool,
        'participants.count': newParticipantCount,
        prizes,
        updatedAt: serverTimestamp()
      })

      return { success: true }
    } catch (error) {
      console.error('Error updating pool:', error)
      return { success: false, error: error.message }
    }
  },

  // Obtener quinielas disponibles (abiertas para unirse)
  fetchAvailableQuinielas: async () => {
    const now = new Date()
    set({ loading: true, error: null })
    try {
      const q = query(
        collection(db, 'quinielas'),
        where('status', 'in', ['upcoming', 'open']),
        orderBy('startDate', 'asc')
      )
      const querySnapshot = await getDocs(q)
      const quinielas = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      set({ quinielas, loading: false })
      return quinielas
    } catch (error) {
      console.error('Error fetching available quinielas:', error)
      set({ error: error.message, loading: false })
      toast.error('Error al cargar quinielas disponibles')
      return []
    }
  },

  // Obtener todas las quinielas (sin filtro de status)
  fetchAllQuinielas: async () => {
    set({ loading: true, error: null })
    
    try {
      const q = query(
        collection(db, 'quinielas'),
        orderBy('createdAt', 'desc')
      )
      
      const querySnapshot = await getDocs(q)
      const quinielas = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))

      set({ quinielas, loading: false })
      return quinielas
    } catch (error) {
      console.error('Error fetching all quinielas:', error)
      set({ error: error.message, loading: false })
      toast.error('Error al cargar quinielas')
      return []
    }
  },

  // Obtener quinielas por status
  fetchQuinielasByStatus: async (status) => {
    set({ loading: true, error: null })
    
    try {
      const q = query(
        collection(db, 'quinielas'),
        where('status', '==', status),
        orderBy('startDate', 'desc')
      )
      
      const querySnapshot = await getDocs(q)
      const quinielas = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))

      set({ quinielas, loading: false })
      return quinielas
    } catch (error) {
      console.error('Error fetching quinielas by status:', error)
      set({ error: error.message, loading: false })
      toast.error('Error al cargar quinielas')
      return []
    }
  },

  // Unirse a una quiniela
  joinQuiniela: async (quinielaId, userId, username) => {
    set({ loading: true, error: null })
    try {
      const quinielaRef = doc(db, 'quinielas', quinielaId)
      const quinielaSnap = await getDoc(quinielaRef)

      if (!quinielaSnap.exists()) {
        throw new Error('Quiniela no encontrada')
      }

      const quinielaData = quinielaSnap.data()

      // Verificar límite de participantes
      if (quinielaData.participants.maxLimit && 
          quinielaData.participants.count >= quinielaData.participants.maxLimit) {
        throw new Error('La quiniela ha alcanzado el límite de participantes')
      }

      // Verificar que esté abierta
      const now = new Date()
      const closeDate = quinielaData.registrationCloseDate?.toDate()
      if (now > closeDate) {
        throw new Error('El registro para esta quiniela ha cerrado')
      }

      // Procesar pago desde la wallet
      const { deductFromWallet } = useWalletStore.getState()
      const paymentResult = await deductFromWallet(
        quinielaData.entryFee,
        `Inscripción a ${quinielaData.name}`,
        quinielaId
      )

      if (!paymentResult.success) {
        throw new Error(paymentResult.error || 'Error al procesar el pago')
      }

      // Crear participación con ID generado automáticamente
      const participantsCollectionRef = collection(db, 'quinielas', quinielaId, 'participants')
      const newParticipantRef = doc(participantsCollectionRef)
      
      const participationData = {
        userId,
        username,
        points: 0,
        correctPredictions: 0,
        paid: true,
        paymentMethod: 'wallet',
        predictions: {},
        position: null,
        prize: 0,
        prizeDistributed: false,
        joinedAt: serverTimestamp()
      }

      await setDoc(newParticipantRef, participationData)

      // Actualizar contador de participantes y premio pool
      const newCount = quinielaData.participants.count + 1
      const newPrizePool = quinielaData.prizePool + quinielaData.entryFee
      const prizes = get().calculatePrizes(newPrizePool, quinielaData.commission)

      await updateDoc(quinielaRef, {
        'participants.count': newCount,
        prizePool: newPrizePool,
        prizes,
        updatedAt: serverTimestamp()
      })

      set({ loading: false })
      toast.success('¡Te has unido a la quiniela exitosamente!')
      return { success: true, participationId: newParticipantRef.id }
    } catch (error) {
      console.error('Error joining quiniela:', error)
      set({ error: error.message, loading: false })
      toast.error(error.message)
      return { success: false, error: error.message }
    }
  },

  // Verificar si el usuario está inscrito (retorna todas las participaciones)
  checkUserParticipation: async (quinielaId, userId) => {
    try {
      const q = query(
        collection(db, 'quinielas', quinielaId, 'participants'),
        where('userId', '==', userId)
      )
      const querySnapshot = await getDocs(q)
      
      const participations = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      
      return {
        isParticipant: participations.length > 0,
        participations,
        participationCount: participations.length
      }
    } catch (error) {
      console.error('Error checking participation:', error)
      return { isParticipant: false, participations: [], participationCount: 0 }
    }
  },

  // Hacer una predicción
  submitPrediction: async (quinielaId, participationId, matchId, prediction, silent = false) => {
    set({ loading: true, error: null })
    try {
      // Verificar que la participación exista
      const participantRef = doc(db, 'quinielas', quinielaId, 'participants', participationId)
      const participantSnap = await getDoc(participantRef)
      
      if (!participantSnap.exists()) {
        throw new Error('Participación no encontrada')
      }

      // Verificar que aún se pueda hacer predicciones
      const quinielaRef = doc(db, 'quinielas', quinielaId)
      const quinielaSnap = await getDoc(quinielaRef)
      const quinielaData = quinielaSnap.data()

      if (quinielaData.status === 'finished' || quinielaData.status === 'cancelled') {
        throw new Error('Esta quiniela ya finalizó')
      }

      // Actualizar predicción
      await updateDoc(participantRef, {
        [`predictions.${matchId}`]: {
          prediction,
          correct: null,
          submittedAt: serverTimestamp()
        }
      })

      set({ loading: false })
      if (!silent) {
        toast.success('Predicción guardada')
      }
      return { success: true }
    } catch (error) {
      console.error('Error submitting prediction:', error)
      set({ error: error.message, loading: false })
      if (!silent) {
        toast.error(error.message)
      }
      return { success: false, error: error.message }
    }
  },

  // Obtener predicciones de una participación específica
  getUserPredictions: async (quinielaId, participationId) => {
    try {
      const participantRef = doc(db, 'quinielas', quinielaId, 'participants', participationId)
      const participantSnap = await getDoc(participantRef)
      
      if (!participantSnap.exists()) {
        return { success: false, predictions: {} }
      }

      const participantData = participantSnap.data()
      return {
        success: true,
        predictions: participantData.predictions || {},
        participantData
      }
    } catch (error) {
      console.error('Error getting predictions:', error)
      return { success: false, predictions: {} }
    }
  },

  // Obtener participantes de una quiniela
  getParticipants: async (quinielaId) => {
    try {
      const q = query(
        collection(db, 'quinielas', quinielaId, 'participants'),
        orderBy('points', 'desc')
      )
      const querySnapshot = await getDocs(q)
      
      const participants = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))

      return { success: true, participants }
    } catch (error) {
      console.error('Error getting participants:', error)
      return { success: false, participants: [] }
    }
  },

  // Obtener partidos de una quiniela
  getQuinielaMatches: async (quinielaId) => {
    try {
      const q = query(
        collection(db, 'quinielas', quinielaId, 'matches'),
        orderBy('date', 'asc')
      )
      const querySnapshot = await getDocs(q)
      
      const matches = querySnapshot.docs.map(doc => {
        const data = doc.data()
        return {
          id: doc.id,
          ...data,
          // Convertir Timestamp a Date para consistencia
          date: data.date?.toDate ? data.date.toDate() : new Date(data.date)
        }
      })

      return { success: true, matches }
    } catch (error) {
      console.error('Error getting matches:', error)
      return { success: false, matches: [] }
    }
  },

  // Obtener resultados de partidos desde la API y actualizar
  syncMatchResults: async (quinielaId) => {
    set({ loading: true, error: null })
    try {
      // Obtener quiniela para obtener league, season y jornada
      const quinielaDoc = await getDoc(doc(db, 'quinielas', quinielaId))
      if (!quinielaDoc.exists()) {
        throw new Error('Quiniela no encontrada')
      }
      const quinielaData = quinielaDoc.data()
      
      // Obtener partidos de la quiniela
      const matchesResult = await get().getQuinielaMatches(quinielaId)
      if (!matchesResult.success || matchesResult.matches.length === 0) {
        throw new Error('No se encontraron partidos para esta quiniela')
      }

      // Obtener IDs de los partidos
      const fixtureIds = matchesResult.matches.map(m => parseInt(m.id))
      
      console.log('🔍 Syncing results for fixtures:', {
        quinielaId,
        league: quinielaData.league,
        season: quinielaData.season,
        jornada: quinielaData.jornada,
        totalMatches: matchesResult.matches.length,
        fixtureIds,
        matches: matchesResult.matches.map(m => ({
          id: m.id,
          home: m.home,
          away: m.away,
          date: m.date
        }))
      })
      
      // Importar dinámicamente el servicio de API
      const { getFixtureResults } = await import('../services/apiFootball.js')
      
      // Obtener resultados de la API (plan de paga permite parámetro ids)
      const results = await getFixtureResults(fixtureIds)
      
      if (results.length === 0) {
        set({ loading: false })
        toast(`ℹ️ No hay partidos finalizados aún. Total de partidos: ${fixtureIds.length}`, {
          icon: '📊',
          duration: 4000
        })
        return { 
          success: true, 
          updatedCount: 0, 
          total: fixtureIds.length,
          message: 'No hay partidos finalizados aún'
        }
      }

      // Actualizar cada partido con su resultado
      let updatedCount = 0
      for (const result of results) {
        console.log('Updating match:', result.id, 'Type:', typeof result.id)
        const matchRef = doc(db, 'quinielas', quinielaId, 'matches', result.id)
        await updateDoc(matchRef, {
          result: result.result,
          status: 'finished',
          updatedAt: serverTimestamp()
        })
        updatedCount++
      }

      set({ loading: false })
      toast.success(`${updatedCount} de ${fixtureIds.length} resultados sincronizados exitosamente`)
      return { success: true, updatedCount, total: fixtureIds.length }
    } catch (error) {
      console.error('Error syncing match results:', error)
      set({ error: error.message, loading: false })
      toast.error(error.message)
      return { success: false, error: error.message }
    }
  },

  // Calcular puntos de todas las participaciones
  calculatePoints: async (quinielaId) => {
    set({ loading: true, error: null })
    try {
      // Obtener partidos con resultados
      const matchesResult = await get().getQuinielaMatches(quinielaId)
      if (!matchesResult.success) {
        throw new Error('Error al cargar partidos')
      }

      const matches = matchesResult.matches.filter(m => m.result)
      if (matches.length === 0) {
        throw new Error('No hay partidos con resultados aún')
      }

      // Obtener todas las participaciones
      const participantsResult = await get().getParticipants(quinielaId)
      if (!participantsResult.success) {
        throw new Error('Error al cargar participantes')
      }

      // Calcular puntos para cada participación
      let updatedCount = 0
      for (const participant of participantsResult.participants) {
        let points = 0
        let correctPredictions = 0

        // Revisar cada predicción
        const predictions = participant.predictions || {}
        
        for (const match of matches) {
          const prediction = predictions[match.id]
          if (prediction?.prediction && match.result?.winner) {
            // Comparar predicción con resultado (1 punto por acierto)
            if (prediction.prediction === match.result.winner) {
              points++
              correctPredictions++
              
              // Actualizar campo correct en la predicción
              predictions[match.id].correct = true
            } else {
              predictions[match.id].correct = false
            }
          }
        }

        // Actualizar participación con puntos
        const participantRef = doc(db, 'quinielas', quinielaId, 'participants', participant.id)
        await updateDoc(participantRef, {
          points,
          correctPredictions,
          predictions,
          updatedAt: serverTimestamp()
        })
        updatedCount++
      }

      set({ loading: false })
      toast.success(`Puntos calculados para ${updatedCount} participantes`)
      return { success: true, updatedCount }
    } catch (error) {
      console.error('Error calculating points:', error)
      set({ error: error.message, loading: false })
      toast.error(error.message)
      return { success: false, error: error.message }
    }
  },

  // Finalizar quiniela y distribuir premios
  finalizeQuiniela: async (quinielaId) => {
    set({ loading: true, error: null })
    try {
      const quinielaRef = doc(db, 'quinielas', quinielaId)
      const quinielaSnap = await getDoc(quinielaRef)
      
      if (!quinielaSnap.exists()) {
        throw new Error('Quiniela no encontrada')
      }

      const quinielaData = quinielaSnap.data()
      
      if (quinielaData.status === 'finished') {
        throw new Error('Esta quiniela ya fue finalizada')
      }

      // Obtener participantes ordenados por puntos
      const participantsResult = await get().getParticipants(quinielaId)
      if (!participantsResult.success || participantsResult.participants.length === 0) {
        // Si no hay participantes, solo marcar como finalizada y salir
        await updateDoc(quinielaRef, {
          status: 'finished',
          finishedAt: serverTimestamp(),
          winners: []
        })
        set({ loading: false })
        toast.success('Quiniela finalizada (sin participantes)')
        return { success: true, winners: [] }
      }

      const sortedParticipants = participantsResult.participants.sort((a, b) => {
        if (b.points !== a.points) return b.points - a.points
        // En caso de empate, usar fecha de inscripción
        return (a.joinedAt?.seconds || 0) - (b.joinedAt?.seconds || 0)
      })

      console.log('🏆 Sorted participants:', sortedParticipants.map(p => ({
        email: p.email,
        points: p.points,
        joinedAt: p.joinedAt
      })))

      // Agrupar participantes por puntajes únicos
      const groupsByPoints = {}
      for (const participant of sortedParticipants) {
        const points = participant.points
        if (!groupsByPoints[points]) {
          groupsByPoints[points] = []
        }
        groupsByPoints[points].push(participant)
      }

      // Obtener los 3 puntajes únicos más altos (ordenados de mayor a menor)
      const uniqueScores = Object.keys(groupsByPoints)
        .map(Number)
        .sort((a, b) => b - a)
        .slice(0, 3)

      console.log('🎯 Unique top scores:', uniqueScores)

      const updates = []
      const prizes = quinielaData.prizes
      const prizesByPlace = [prizes.first, prizes.second, prizes.third]

      // Asignar premios según los 3 puntajes más altos
      for (let i = 0; i < uniqueScores.length; i++) {
        const score = uniqueScores[i]
        const group = groupsByPoints[score]
        const groupSize = group.length
        const place = i + 1 // 1er, 2do, 3er lugar
        const totalPrize = prizesByPlace[i]
        const prizePerPerson = Math.floor(totalPrize / groupSize)

        console.log(`📊 Lugar ${place}: ${groupSize} ganador(es) con ${score} puntos - $${prizePerPerson} c/u (Total: $${totalPrize})`)

        // Asignar premio a todos los miembros del grupo
        for (const member of group) {
          updates.push({
            id: member.id,
            userId: member.userId,
            position: place,
            prize: prizePerPerson
          })
        }
      }

      // Asignar posición a participantes que no ganaron premio
      let nextPosition = uniqueScores.length + 1
      const scoresWithPrizes = new Set(uniqueScores)
      
      for (const score of Object.keys(groupsByPoints).map(Number).sort((a, b) => b - a)) {
        if (!scoresWithPrizes.has(score)) {
          for (const member of groupsByPoints[score]) {
            updates.push({
              id: member.id,
              userId: member.userId,
              position: nextPosition,
              prize: 0
            })
          }
          nextPosition++
        }
      }

      console.log('💰 Prize distribution:', updates)

      // Aplicar actualizaciones
      const batch = []
      for (const update of updates) {
        const participantRef = doc(db, 'quinielas', quinielaId, 'participants', update.id)
        await updateDoc(participantRef, {
          position: update.position,
          prize: update.prize,
          prizeDistributed: false
        })

        // Si ganó algún premio, agregarlo a la wallet del usuario
        if (update.prize > 0) {
          const userRef = doc(db, 'users', update.userId)
          const userSnap = await getDoc(userRef)
          const userData = userSnap.data()
          const currentWinnings = userData?.wallet?.winnings || 0

          await updateDoc(userRef, {
            'wallet.winnings': currentWinnings + update.prize,
            'wallet.total': (userData?.wallet?.deposited || 0) + currentWinnings + update.prize
          })

          // Crear registro de transacción en colección de nivel superior
          await addDoc(collection(db, 'transactions'), {
            userId: update.userId,
            type: 'prize',
            amount: update.prize,
            description: `Premio ${update.position}° lugar - ${quinielaData.name}`,
            relatedQuiniela: quinielaId,
            createdAt: serverTimestamp(),
            status: 'completed'
          })

          batch.push(update)
        }
      }

      // Marcar quiniela como finalizada
      await updateDoc(quinielaRef, {
        status: 'finished',
        finishedAt: serverTimestamp(),
        winners: updates.filter(u => u.prize > 0).map(u => ({
          userId: u.userId,
          position: u.position,
          prize: u.prize
        }))
      })

      set({ loading: false })
      toast.success(`Quiniela finalizada. Premios distribuidos a ${batch.length} ganadores`)
      return { success: true, winners: batch }
    } catch (error) {
      console.error('Error finalizing quiniela:', error)
      set({ error: error.message, loading: false })
      toast.error(error.message)
      return { success: false, error: error.message }
    }
  },

  // Limpiar estado
  clearCurrentQuiniela: () => set({ currentQuiniela: null }),
  clearError: () => set({ error: null })
}))
